import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import authSlice from '../utils/redux/slice/authSlice';
import { clientActions } from '../actions/client';
import { getUroSocket } from '../services/URO';

const BALANCE_KEY = 'uro_cached_balance';

/* Module-level profile cache: every screen mounts this hook, which used to mean
   one GET /client/profile per navigation. 30s TTL + in-flight promise sharing.
   Socket balance pushes must patch the cache (patchCachedBalance) or the next
   mount would serve the pre-spend balance back. The expectedEmail check busts
   the cache across a logout/login user switch. */
const PROFILE_TTL_MS = 30_000;
const EMPTY_CACHE = { data: null, email: null, ts: 0, promise: null, promiseEmail: null };
let profileCache = { ...EMPTY_CACHE };
// Socket pushes are the freshest balance source. Stamped here so a profile
// fetch that STARTED before a push can never rewind the balance when it
// resolves late (fetch results are corrected against this before use).
let lastBalancePatch = { ts: 0, value: null };

function patchCachedBalance(b) {
  lastBalancePatch = { ts: Date.now(), value: b };
  if (profileCache.data?.data) profileCache.data.data.client_balance = b;
}

export function clearProfileCache() {
  profileCache = { ...EMPTY_CACHE };
  lastBalancePatch = { ts: 0, value: null };
  try { localStorage.removeItem(BALANCE_KEY); } catch {}
}

export function getProfileCached(expectedEmail) {
  const fresh = profileCache.data && Date.now() - profileCache.ts < PROFILE_TTL_MS;
  const sameUser = !expectedEmail || !profileCache.email || profileCache.email === expectedEmail;
  if (fresh && sameUser) return Promise.resolve(profileCache.data);
  // Only share an in-flight fetch that was started for the same user —
  // otherwise a fast logout/login could hand user B user A's profile.
  const samePromiseUser =
    !expectedEmail || !profileCache.promiseEmail || profileCache.promiseEmail === expectedEmail;
  if (profileCache.promise && samePromiseUser) return profileCache.promise;
  const startedAt = Date.now();
  const promise = clientActions.getClientProfile()
    .then((res) => {
      // A socket balance push newer than this fetch's start wins — correct the
      // response in place so both the cache and every consumer see it.
      if (res?.data && lastBalancePatch.ts > startedAt) {
        res.data.client_balance = lastBalancePatch.value;
      }
      // Commit only if still the current fetch — a fetch superseded by
      // clearProfileCache() (logout) must not resurrect the old user's cache.
      if (profileCache.promise === promise) {
        profileCache = {
          data: res,
          email: res?.data?.client_email || null,
          ts: Date.now(),
          promise: null,
          promiseEmail: null,
        };
      }
      return res;
    })
    .catch((err) => {
      if (profileCache.promise === promise) {
        profileCache.promise = null;
        profileCache.promiseEmail = null;
      }
      throw err;
    });
  profileCache.promise = promise;
  profileCache.promiseEmail = expectedEmail || null;
  return promise;
}

function readCachedBalance(reduxBalance) {
  if (reduxBalance != null) return reduxBalance;
  const cached = localStorage.getItem(BALANCE_KEY);
  return cached !== null ? Number(cached) : 0;
}

export default function useClientData() {
  const reduxUser = useSelector(authSlice.selectors.getUser);

  const [balance, setBalance] = useState(() => readCachedBalance(reduxUser?.client_balance));
  const [profile, setProfile] = useState({
    name: reduxUser?.client_name || 'User',
    email: reduxUser?.client_email || '',
  });
  const socketRef = useRef(null);

  // Fetch results go through applyBalance only (getProfileCached already
  // corrects them against newer socket pushes); socket pushes additionally
  // stamp the module cache via patchCachedBalance.
  function applyBalance(b) {
    setBalance(b);
    try { localStorage.setItem(BALANCE_KEY, String(b)); } catch {}
  }

  useEffect(() => {
    let mounted = true;

    async function fetchProfile() {
      try {
        const res = await getProfileCached(reduxUser?.client_email);
        if (!mounted) return;
        const d = res?.data;
        applyBalance(d?.client_balance ?? 0);
        setProfile({ name: d?.client_name || 'User', email: d?.client_email || '' });
      } catch {}
    }

    fetchProfile();

    const socket = getUroSocket();
    socketRef.current = socket;
    if (socket?.connect) socket.connect();

    const onBalance = (data) => {
      const b = data?.client_balance?.client_balance ?? data?.client_balance;
      if (b !== undefined) {
        patchCachedBalance(b);
        applyBalance(b);
      }
    };
    socket.on('client:balance_changed', onBalance);

    return () => {
      mounted = false;
      if (socket?.off) socket.off('client:balance_changed', onBalance);
    };
  }, []); // eslint-disable-line

  return { balance, clientName: profile.name, clientEmail: profile.email };
}
